import React from 'react';
import { useSelector } from 'react-redux';
import { grey } from '@mui/material/colors';
import { InputField } from 'components/form';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { ListingForm } from 'components/shared/TableListing';
import { Box, IconButton, Stack, Tooltip, Typography, Autocomplete, Chip, TextField } from '@mui/material';
import { DeleteOutline, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

import { Recipient, RecipientRole, StagesFormValues } from '../../../../types';
import { UserProfile } from '@trnsact/trnsact-shared-types';

interface Props {
  isEdit: boolean;
  rowIndex: number;
  onRemove: VoidFunction;
  onMoveRow: (from: number, to: number) => void;
}

export const TitleCell = ({ rowIndex, onRemove, onMoveRow, isEdit }: Props) => {
  const { control, getValues } = useFormContext<ListingForm<StagesFormValues>>();
  const rows = useWatch({ control, name: 'bodyFields' });

  const [recipients, setRecipients] = React.useState<Recipient[]>([]);

  const RECIPIENT_OPTIONS: Recipient[] = [
    { role: RecipientRole.SALES_MANAGER, name: 'Sales Manager', userProfileId: undefined },
    { role: RecipientRole.SALES_REP, name: 'Sales Rep', userProfileId: undefined },
    { role: RecipientRole.FINANCE_MANAGER, name: 'Finance Manager', userProfileId: undefined },
  ].concat(
    useSelector((state: any) => state.relatedUsers)
      .filter((u: UserProfile) => u.status !== 'blocked')
      .map((u: UserProfile) => ({ userProfileId: u.id, name: u.fullName, role: undefined }))
  );

  return (
    <Stack
      direction="row"
      gap="0.75rem"
      width="100%"
      alignItems="flex-start"
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        p: '12px',
        boxSizing: 'border-box',
      }}
    >
      {/* left number */}
      <Box
        sx={{
          borderRadius: '999px',
          height: 28,
          minWidth: 28,
          px: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: grey[200],
          mt: '6px',
          flex: '0 0 auto',
        }}
      >
        <Typography variant="caption">{rowIndex + 1}</Typography>
      </Box>

      {/* middle content (stretches full width) */}
      <Stack direction="column" gap="0.5rem" sx={{ flex: 1, minWidth: 0 }}>
        {/* stage name row */}
        <Box sx={{ width: '100%', maxWidth: 340 }}>
          <InputField
            control={control}
            name={`bodyFields.${rowIndex}.name`}
            inputProps={{
              variant: 'outlined',
              placeholder: 'Enter stage name',
              required: true,
              size: 'small',
            }}
          />
        </Box>

        {/* notification recipients row (fills width) */}
        <Box sx={{ width: '100%' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: '4px' }}>
            Notification Recipients
          </Typography>

          <Controller
            control={control}
            name={`bodyFields.${rowIndex}.recipients` as const}
            defaultValue={[]}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={RECIPIENT_OPTIONS}
                value={field.value ?? []}
                onChange={(_, next) => field.onChange(next)}
                disableCloseOnSelect
                filterSelectedOptions
                fullWidth
                getOptionLabel={o => o.name ?? ''}
                isOptionEqualToValue={(opt, val) =>
                  `${opt.userProfileId ?? ''}-${opt.role ?? ''}` === `${val.userProfileId ?? ''}-${val.role ?? ''}`
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={`${option.userProfileId ?? ''}-${option.role ?? ''}-${option.name}`}
                      label={option.name}
                      size="small"
                    />
                  ))
                }
                renderOption={(props, option) => (
                  <li {...props} key={`${option.userProfileId ?? ''}-${option.role ?? ''}-${option.name}`}>
                    <Typography variant="body2">{option.name}</Typography>
                  </li>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    hiddenLabel
                    placeholder={(field.value?.length ?? 0) ? '' : 'Select recipients'}
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': { minHeight: 40 },
                      '& .MuiAutocomplete-input': { py: 0 },
                    }}
                  />
                )}
              />
            )}
          />

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: '4px' }}>
            Selected recipients will be notified when the stage is completed
          </Typography>
        </Box>
      </Stack>

      {/* right icons */}
      {!isEdit && (
        <Stack direction="row" alignItems="center" sx={{ ml: 'auto', mt: '2px', flex: '0 0 auto' }}>
          <Tooltip title="Remove configuration">
            <IconButton size="small" color="error" onClick={onRemove}>
              <DeleteOutline />
            </IconButton>
          </Tooltip>

          <Tooltip title="Move row down">
            <IconButton
              size="small"
              disabled={rowIndex + 1 > rows.length - 1}
              onClick={() => onMoveRow(rowIndex, rowIndex + 1)}
            >
              <KeyboardArrowDown />
            </IconButton>
          </Tooltip>

          <Tooltip title="Move row up">
            <IconButton size="small" disabled={!rowIndex} onClick={() => onMoveRow(rowIndex, rowIndex - 1)}>
              <KeyboardArrowUp />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Stack>
  );
};
